import {
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import {
    DateConstants,
    forLifeOf,
    IDateRange,
    LifecycleAware,
    Styling,
    SubscriptionLike,
    TourHelperService
} from '@lsq-frontend/common-ui';
import {
    FeatureFlagService,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { combineLatest, Subject } from 'rxjs';
import { delay, filter, first, map } from 'rxjs/operators';
import { FeatureFlags } from '@lsq-frontend/fasttrack-shared/app-constants';
import { TourAnalyticsNames, TourIds, TourSteps } from '../../constants/tours.constants';
import { GetReserveReleasesService } from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import {
    GetReserveReleasesRequest,
    GetReserveReleasesResponse
} from '@lsq-frontend/remote-services/enterprise/common';
import { startOfDay, subDays } from 'date-fns';

@LifecycleAware()
@Component({
    selector: 'request-funds',
    template: `
        <div class="request-funds-page card-table-layout">
            <lsq-banner [titleText]="title"> </lsq-banner>

            <div class="container-fluid page-container">
                <div class="row">
                    <div class="col-xl-4">
                        <lsq-sticky-wrapper
                            [stickyEnabled]="
                                (responsiveService.xl | async) && !isStickyCardTallestItem
                            "
                        >
                            <div class="request-funds-section" #stickyCardRef>
                                <request-funds-card
                                    [isMobile]="isMobile"
                                    (update)="dispatchUpdateReserveReleasesTable()"
                                    (loaded)="handleCardLoaded()"
                                >
                                </request-funds-card>
                            </div>
                        </lsq-sticky-wrapper>
                    </div>
                    <div class="col-xl-8">
                        <div class="request-funds-section" #tableRef>
                            <request-funds-table [dataSource]="getReserveReleasesDataSource">
                            </request-funds-table>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `,
    styleUrls: ['./request-funds.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class RequestFundsComponent implements OnInit, OnDestroy {
    @ViewChild('stickyCardRef') public stickyCardRef: ElementRef;
    @ViewChild('tableRef') public tableRef: ElementRef;
    public getReserveReleasesDataSource: LsqApiServiceListSource<
        GetReserveReleasesResponse,
        GetReserveReleasesRequest
    >;
    public filterDateRange: IDateRange;
    public title = this._i18n({
        value: 'Request Funds',
        meaning: 'Request Funds page title'
    });
    public isMobile = false;
    public updateTableSubject = new Subject<void>();
    public updateTableStream = this.updateTableSubject.asObservable();
    public isStickyCardTallestItem = false;
    public windowScrollSubscription: SubscriptionLike;
    private hasCardLoaded = false;

    constructor(
        private _i18n: I18n,
        public getReserveReleasesService: GetReserveReleasesService,
        public responsiveService: ResponsiveService,
        private _windowReferenceService: WindowReferenceService,
        private _featureFlagsService: FeatureFlagService,
        private _tourHelperService: TourHelperService
    ) {
        this.responsiveService.smBelow.pipe(forLifeOf(this)).subscribe((isMobile) => {
            if (!isMobile) {
                if (!this.windowScrollSubscription) {
                    this.windowScrollSubscription = this._windowReferenceService
                        .createScrollStream(true)
                        .subscribe(() => {
                            if (this.stickyCardRef && this.stickyCardRef.nativeElement) {
                                this.isStickyCardTallestItem =
                                    this.stickyCardRef.nativeElement.offsetHeight >=
                                    this.tableRef.nativeElement.offsetHeight;
                            }
                        });
                }
            }

            this.isMobile = isMobile;
        });
    }
    public ngOnInit(): void {
        this.getRequestReserveRelease();
    }

    public getRequestReserveRelease() {
        this.filterDateRange = {
            start: DateConstants.BeginningOfTime,
            end: startOfDay(new Date())
        };
        const getReserveReleasesInitialRequest = new GetReserveReleasesRequest({
            fromDateFilter: this.filterDateRange.start,
            toDateFilter: this.filterDateRange.end,
            take: 25
        });

        this.getReserveReleasesDataSource = new LsqApiServiceListSource(
            this.getReserveReleasesService,
            getReserveReleasesInitialRequest
        );
    }
    public ngOnDestroy(): void {
        if (this.windowScrollSubscription) {
            this.windowScrollSubscription.unsubscribe();
        }
    }

    public handleCardLoaded(): void {
        if (!this.hasCardLoaded) {
            combineLatest(
                this._featureFlagsService.isOn(FeatureFlags.RequestFundsTips),
                this.responsiveService.smBelow
            )
                .pipe(
                    first(),
                    filter(([flagIsOn, isMobile]) => !!flagIsOn && !isMobile),
                    map(() => {
                        const tourSteps = TourSteps.createRequestsFundsTourSteps();
                        // Let the page render before starting tour.
                        return this._tourHelperService.attachEndEvents(
                            {
                                tourId: TourIds.RequestFundsTips,
                                minimumScreenSize: Styling.mdStart,
                                steps: tourSteps
                            },
                            TourAnalyticsNames.RequestFundsTips
                        );
                    }),
                    delay(0),
                    forLifeOf(this)
                )
                .subscribe((requestFundsTour) =>
                    this._tourHelperService.startTour(requestFundsTour)
                );
        }
        this.hasCardLoaded = true;
    }

    public dispatchUpdateReserveReleasesTable(requestOverride?: GetReserveReleasesRequest): void {
        // The request is cloned because the ApiService has a `distinctUntilChanged` filter on the request stream.
        this.getReserveReleasesDataSource.dispatchRequest({ ...requestOverride });
    }
}
